import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Edit, Trash2, X, Tag, Star, FileSpreadsheet } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import ImageUploader from '../components/ImageUploader';
import PromotionItemsEditor, { calcDiscount } from '../components/PromotionItemsEditor';
import PromotionImportDialog from '../components/PromotionImportDialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// QA-added (was missing/undefined and crashed /admin/promocoes)
const getPromoStatus = (promo) => {
  const today = new Date().toISOString().slice(0, 10);
  const start = (promo.start_date || '').slice(0, 10);
  const end = (promo.end_date || '').slice(0, 10);
  if (!promo.is_active) return { label: 'Inativa', class: 'bg-neutral-200 text-neutral-700' };
  if (end && today > end) return { label: 'Expirada', class: 'bg-red-100 text-red-700' };
  if (start && today < start) return { label: 'Programada', class: 'bg-blue-100 text-blue-700' };
  return { label: 'Ativa', class: 'bg-emerald-100 text-emerald-700' };
};


const maxDiscount = (promo) => {
  const fromItems = (promo.items || []).map((i) => i.discount_percentage || 0);
  return Math.round(Math.max(promo.discount_percentage || 0, ...fromItems, 0));
};

// Converte promoções antigas (% geral) em itens com preço individual
const toEditableItems = (promo, products) => {
  if (promo.items?.length) return promo.items.map((i) => ({ ...i }));
  const d = promo.discount_percentage || 0;
  return (promo.product_ids || []).map((pid) => {
    const p = products.find((x) => x.id === pid);
    const base = p?.price || 0;
    return { product_id: pid, sku: p?.sku || '', original_price: base, promo_price: d > 0 ? Math.round(base * (1 - d / 100) * 100) / 100 : '' };
  });
};

const AdminPromotions = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [promotions, setPromotions] = useState([]);
  const [weeklyHighlights, setWeeklyHighlights] = useState({ title: '', subtitle: '', product_ids: [] });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [activeTab, setActiveTab] = useState('promotions'); // 'promotions' or 'weekly'
  const [importingPromotion, setImportingPromotion] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    banner_image: '',
    start_date: '',
    end_date: '',
    items: [],
    is_active: true
  });

  useEffect(() => {
    if (authLoading) return;
    if (!user || !user.is_admin) {
      toast.error('Acesso negado');
      navigate('/');
      return;
    }
    fetchData();
  }, [authLoading, user, navigate]);

  const fetchData = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const [promoRes, productsRes, highlightsRes] = await Promise.all([
        axios.get(`${API_URL}/promotions/all`),
        axios.get(`${API_URL}/products?limit=2000`),
        axios.get(`${API_URL}/weekly-highlights`)
      ]);
      setPromotions(promoRes.data);
      setProducts(productsRes.data);
      setWeeklyHighlights(highlightsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      banner_image: '',
      start_date: '',
      end_date: '',
      items: [],
      is_active: true
    });
    setEditingPromotion(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const invalid = formData.items.find((i) => calcDiscount(i.original_price, i.promo_price) === null);
    if (invalid) {
      toast.error('Cada produto precisa de um preço promocional menor que o preço anterior');
      return;
    }
    const payload = {
      ...formData,
      discount_percentage: 0,
      product_ids: formData.items.map((i) => i.product_id),
      items: formData.items.map((i) => ({ ...i, original_price: parseFloat(i.original_price), promo_price: parseFloat(i.promo_price) })),
    };
    try {
      if (editingPromotion) {
        await axios.put(`${API_URL}/promotions/${editingPromotion.id}`, payload);
        toast.success('Promoção atualizada!');
      } else {
        await axios.post(`${API_URL}/promotions`, payload);
        toast.success('Promoção criada!');
      }
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar promoção');
    }
  };

  const handleEdit = (promotion) => {
    setFormData({
      title: promotion.title,
      description: promotion.description,
      banner_image: promotion.banner_image || '',
      start_date: promotion.start_date || '',
      end_date: promotion.end_date || '',
      items: toEditableItems(promotion, products),
      is_active: promotion.is_active !== false
    });
    setEditingPromotion(promotion);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta promoção?')) return;
    try {
      await axios.delete(`${API_URL}/promotions/${id}`);
      toast.success('Promoção excluída!');
      fetchData();
    } catch (error) {
      toast.error('Erro ao excluir');
    }
  };

  const handleSaveWeeklyHighlights = async () => {
    try {
      await axios.put(`${API_URL}/weekly-highlights`, weeklyHighlights);
      toast.success('Destaques da semana atualizados!');
    } catch (error) {
      toast.error('Erro ao salvar destaques');
    }
  };

  const toggleProductInHighlights = (productId) => {
    const ids = weeklyHighlights.product_ids.includes(productId)
      ? weeklyHighlights.product_ids.filter(id => id !== productId)
      : [...weeklyHighlights.product_ids, productId];
    setWeeklyHighlights({ ...weeklyHighlights, product_ids: ids });
  };

  if (authLoading || loading) {
    return <div className="py-20 text-center"><p className="text-muted-foreground">Carregando...</p></div>;
  }

  return (
    <div data-testid="admin-promotions" className="py-20 md:py-32">
      <div className="px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin')} className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
              Promoções & Destaques
            </h1>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b border-border">
          <button
            onClick={() => setActiveTab('promotions')}
            className={`pb-4 px-2 font-accent text-xs uppercase tracking-widest transition-colors ${
              activeTab === 'promotions' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'
            }`}
          >
            <Tag className="w-4 h-4 inline mr-2" />
            Promoções
          </button>
          <button
            onClick={() => setActiveTab('weekly')}
            className={`pb-4 px-2 font-accent text-xs uppercase tracking-widest transition-colors ${
              activeTab === 'weekly' ? 'border-b-2 border-primary text-primary' : 'text-muted-foreground'
            }`}
          >
            <Star className="w-4 h-4 inline mr-2" />
            Destaques da Semana
          </button>
        </div>

        {/* Promoções Tab */}
        {activeTab === 'promotions' && (
          <div>
            <div className="flex justify-end mb-8">
              <button
                onClick={() => { setShowForm(true); resetForm(); }}
                data-testid="new-promotion-btn"
                className="flex items-center gap-2 bg-primary text-white px-6 py-3 uppercase tracking-widest text-xs hover:bg-black transition-all"
              >
                <Plus className="w-4 h-4" />
                Nova Promoção
              </button>
            </div>

            {/* Lista de Promoções */}
            <div className="space-y-4">
              {promotions.length === 0 ? (
                <p className="text-center text-muted-foreground py-12">Nenhuma promoção cadastrada</p>
              ) : (
                promotions.map((promo) => {
                  const status = getPromoStatus(promo);
                  return (
                  <div key={promo.id} className="border border-border p-6 flex items-center justify-between">
                    <div className="flex items-center gap-6">
                      {promo.banner_image && (
                        <img src={promo.banner_image} alt="" className="w-20 h-20 object-cover" />
                      )}
                      <div>
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="font-medium text-lg">{promo.title}</h3>
                          <span data-testid={`promo-status-${promo.id}`} className={`text-xs px-2 py-1 rounded ${status.class}`}>
                            {status.label}
                          </span>
                          {maxDiscount(promo) > 0 && (
                            <span data-testid={`promo-max-discount-${promo.id}`} className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                              até -{maxDiscount(promo)}%
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{promo.description}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {(promo.items?.length || promo.product_ids?.length || 0)} produtos
                          {(promo.start_date || promo.end_date) && ` · ${promo.start_date?.slice(0,10) || 'início imediato'} até ${promo.end_date?.slice(0,10) || 'sem data final'}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setImportingPromotion(promo)} data-testid={`promo-import-btn-${promo.id}`} className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest border border-border hover:bg-muted">
                        <FileSpreadsheet className="w-4 h-4" />
                        <span className="hidden sm:inline">Importar planilha</span>
                      </button>
                      <button onClick={() => handleEdit(promo)} data-testid={`promo-edit-btn-${promo.id}`} className="p-2 text-primary hover:bg-muted rounded">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button onClick={() => handleDelete(promo.id)} className="p-2 text-red-500 hover:bg-red-50 rounded">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Destaques da Semana Tab */}
        {activeTab === 'weekly' && (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Título da Seção
                </label>
                <input
                  type="text"
                  value={weeklyHighlights.title || ''}
                  onChange={(e) => setWeeklyHighlights({ ...weeklyHighlights, title: e.target.value })}
                  placeholder="Destaques da Semana"
                  className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3"
                />
              </div>
              <div>
                <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                  Subtítulo
                </label>
                <input
                  type="text"
                  value={weeklyHighlights.subtitle || ''}
                  onChange={(e) => setWeeklyHighlights({ ...weeklyHighlights, subtitle: e.target.value })}
                  placeholder="Seleção especial para você"
                  className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3"
                />
              </div>
            </div>

            <div>
              <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-4 block">
                Selecione os Produtos em Destaque
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {products.map((product) => (
                  <div
                    key={product.id}
                    onClick={() => toggleProductInHighlights(product.id)}
                    className={`cursor-pointer border-2 p-2 transition-all ${
                      weeklyHighlights.product_ids?.includes(product.id)
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-primary/50'
                    }`}
                  >
                    <img
                      src={product.images?.[0] || '/placeholder.svg'}
                      alt={product.name}
                      className="w-full aspect-square object-cover mb-2"
                    />
                    <p className="text-xs font-medium truncate">{product.name}</p>
                    <p className="text-xs text-muted-foreground">
                      R$ {product.price?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={handleSaveWeeklyHighlights}
              className="bg-primary text-white px-8 py-3 uppercase tracking-widest text-xs hover:bg-black transition-all"
            >
              Salvar Destaques
            </button>
          </div>
        )}

        {importingPromotion && (
          <PromotionImportDialog
            promotion={importingPromotion}
            onClose={() => setImportingPromotion(null)}
            onImported={() => fetchData(true)}
          />
        )}

        {/* Modal de Promoção */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-background w-full max-w-3xl max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-background p-6 border-b border-border flex justify-between items-center">
                <h2 className="font-heading text-2xl font-light">
                  {editingPromotion ? 'Editar Promoção' : 'Nova Promoção'}
                </h2>
                <button onClick={() => { setShowForm(false); resetForm(); }}>
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div>
                  <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                    Título da Promoção *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    data-testid="promo-title-input"
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="Ex: Promoção de Natal, Black Friday"
                    className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3"
                  />
                </div>

                <div>
                  <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                    Descrição
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descreva a promoção..."
                    rows={3}
                    className="w-full bg-transparent border border-border focus:border-primary p-4 resize-none"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                      Data Início
                    </label>
                    <input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3"
                    />
                  </div>
                  <div>
                    <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                      Data Fim
                    </label>
                    <input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      className="w-full bg-transparent border-b border-border focus:border-primary px-0 py-3"
                    />
                  </div>
                </div>

                <div>
                  <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-4 block">
                    Banner da Promoção
                  </label>
                  <ImageUploader
                    existingImages={formData.banner_image ? [formData.banner_image] : []}
                    onUploadComplete={(urls) => setFormData({ ...formData, banner_image: urls[0] || '' })}
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="is_active" className="text-sm">Promoção Ativa</label>
                </div>

                <div>
                  <label className="font-accent text-xs uppercase tracking-widest text-muted-foreground mb-2 block">
                    Produtos e preços promocionais
                  </label>
                  <p className="text-xs text-muted-foreground mb-4">
                    Cada produto tem seu próprio preço. O desconto (%) é calculado automaticamente. Para muitos itens, salve e use “Importar planilha”.
                  </p>
                  <PromotionItemsEditor
                    products={products}
                    items={formData.items}
                    onChange={(items) => setFormData((f) => ({ ...f, items }))}
                  />
                </div>

                <div className="flex gap-4 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); resetForm(); }}
                    className="flex-1 border border-border px-6 py-3 uppercase tracking-widest text-xs hover:bg-muted transition-all"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    data-testid="promo-submit-btn"
                    className="flex-1 bg-primary text-white px-6 py-3 uppercase tracking-widest text-xs hover:bg-black transition-all"
                  >
                    {editingPromotion ? 'Salvar' : 'Criar Promoção'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPromotions;
